import { createContext, useCallback, useMemo } from 'react';

// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { useTreatmentTypeSettings } from 'contexts/structure-edit/useTreatmentTypeSettings';
import {
  AvailableLayoutModule,
  AvailableRequiredLayoutModule,
  IssueContainer,
  Layout,
  NewsletterPageContainer,
  PageContainer,
  PageModule,
  PageType,
  PageTypeSetting,
  SectionContainer,
  TreatmentTypeSetting,
  UiModule,
  UiModuleType,
  usePageTypeSettingByPublicationKeyPageTypeQuery
} from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';

export type ModuleInfo = {
  minimumAllowed: number | null | undefined;
  maximumAllowed: number | null | undefined;
  uiModuleType: UiModuleType;
};

interface PageTypeSettingsContext {
  pageTypeSetting: PageTypeSetting | undefined;
  currentLayout: Layout | undefined;
  addableUiModules: AvailableLayoutModule[] | undefined;
  removableUiModules: (AvailableLayoutModule | AvailableRequiredLayoutModule)[];
  availableRequiredLayoutModules: AvailableRequiredLayoutModule[] | undefined;
  getTreatmentTypeSetting: (pageModule: PageModule) => TreatmentTypeSetting | undefined;
  structureTreatmentTypes: TreatmentTypeSetting[];
  isSection: boolean;
  isNewsletter: boolean;
  addedModulesByTreatmentType: Record<string, number>;
  moduleInfoByTreatmentType: Record<string, ModuleInfo>;
}

const DEFAULT_STATE: PageTypeSettingsContext = {
  pageTypeSetting: {} as PageTypeSetting,
  currentLayout: {} as Layout,
  addableUiModules: [],
  removableUiModules: [],
  availableRequiredLayoutModules: [],
  getTreatmentTypeSetting: () => undefined,
  structureTreatmentTypes: [],
  isSection: false,
  isNewsletter: false,
  addedModulesByTreatmentType: {},
  moduleInfoByTreatmentType: {}
};

interface PageTypeSettingsProviderProps {
  children: React.ReactNode;
  pageType?: PageType;
}

export const PageTypeSettingsContext = createContext(DEFAULT_STATE);

const getTreatmentType = (pageModule: PageModule): string => {
  let treatmentType = null;
  // uiModuleFields is null on structure pages until hydrated at render time; return early with no treatment type.
  if (!pageModule.uiModuleFields) return '';
  const keys = Object.keys(pageModule.uiModuleFields);
  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    const uiModuleFieldKey = key as keyof UiModule;
    const uiModule = pageModule.uiModuleFields[uiModuleFieldKey] as { treatmentType: string } | null;
    if (uiModule) {
      treatmentType = uiModule.treatmentType;
    }
  }
  return treatmentType as string;
};

export const getNewsletterLayoutConfigById = (newsletterId: string, pageTypeSetting: PageTypeSetting | undefined) => {
  if (!pageTypeSetting) {
    return null;
  }

  const specificLayout = pageTypeSetting.layouts.find((l) => l.id === String(newsletterId));

  if (specificLayout) {
    return specificLayout;
  }

  return pageTypeSetting.layouts.find((l) => l.id === 'baseline' || l.id === 'default') ?? null;
};

export const PageTypeSettingsProvider = ({ pageType, children }: PageTypeSettingsProviderProps) => {
  const currentProperty = useConvertedProperty();
  const structureTreatmentTypes = useTreatmentTypeSettings(currentProperty ?? '');
  const { root, metadata, getAllGroupConfigs } = useDataModelContext<IssueContainer | SectionContainer>();
  const hasStructure = getAllGroupConfigs().length > 0;

  const pageContainers: NewsletterPageContainer | PageContainer | undefined = useMemo(() => {
    if (root.type === 'Issue') {
      return (root as IssueContainer).collection[0].collection[0];
    }

    // NewsletterPageContainer has the same structure as PageContainer
    if (root.type === 'Page' && root.subtype === 'NewsletterPage') {
      // SectionContainer.collection[0] === PageContainer, so:
      return root as unknown as NewsletterPageContainer;
    }

    return (root as SectionContainer).collection[0];
  }, [root]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const layoutId = useMemo(() => pageContainers?.attributes.layoutId, [pageContainers]);

  // CLEANUP(structure-migration): remove once layoutId migration is confirmed stable across all pubs
  // if layoutId looks like a UUID, try to calculate the correct layoutId from allessehId
  const resolvedLayoutId = useMemo(() => {
    if (!layoutId) {
      return layoutId;
    }

    // ITP metadata.allessehId has the format UCSD_WSJ_ITP_ITP{YYYYMMDD}, which would produce
    // an incorrect date-based layoutId (e.g. itp_itp20260207). Skip the calculation for ITP.
    if (root.type === 'Issue') {
      return layoutId;
    }

    // check if layoutId is a UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (uuidRegex.test(layoutId)) {
      const calculatedLayoutId = metadata.allessehId.split('_').slice(2).join('_').toLowerCase();

      if (
        currentProperty === 'barrons' &&
        pageType === PageType.Section &&
        layoutId === '83fae478-0f08-42fd-851a-6f0acfc9ae84' &&
        calculatedLayoutId === 'advisor_center'
      ) {
        return 'advisor-legacy';
      }
      if (calculatedLayoutId === '') {
        return layoutId;
      }

      return calculatedLayoutId;
    }

    return layoutId;
  }, [layoutId, metadata, root.type, currentProperty, pageType]);
  // END CLEANUP(structure-migration)

  const { data: pageTypeSettingsResp } = usePageTypeSettingByPublicationKeyPageTypeQuery(
    {
      publicationKey: currentProperty ?? '',
      pageType: pageType ?? PageType.Home,
      layoutId: resolvedLayoutId,
      hasStructure: hasStructure || undefined
    },
    { enabled: !!currentProperty }
  );

  const getAllUiModuleTypes = useCallback(
    (pageModules?: PageModule[]): UiModuleType[] =>
      (pageModules ?? []).reduce<UiModuleType[]>(
        (acc, curr) => (curr.uiModuleType ? acc.concat([curr.uiModuleType]) : acc),
        []
      ),
    []
  );

  const pageTypeSetting = useMemo(
    () => pageTypeSettingsResp?.pageTypeSettingByPublicationKeyPageType as PageTypeSetting | undefined,
    [pageTypeSettingsResp]
  );

  const currentLayout = useMemo(() => {
    if (!pageTypeSetting) {
      return undefined;
    }

    if (pageType === PageType.Banner) {
      return undefined;
    }

    if (pageContainers.subtype === 'NewsletterPage') {
      const newsletterContainer = pageContainers as NewsletterPageContainer;
      return (
        getNewsletterLayoutConfigById(String(newsletterContainer.attributes.newsletterId), pageTypeSetting) ?? undefined
      );
    }

    // CLEANUP(structure-migration): uncomment this and delete the block below once layoutId migration is stable
    // const pageLayout = pageTypeSetting.layouts.find((l) => l.id === pageContainers?.attributes.layoutId);

    // CLEANUP(structure-migration): remove once layoutId migration is confirmed stable across all pubs and no longer needed
    // during layoutId migration, some pages may have ambiguous UUIDs
    // `pageContainers` can be undefined
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    const matchingLayouts = pageTypeSetting.layouts.filter((l) => l.id === resolvedLayoutId);

    let pageLayout: Layout | undefined;

    if (matchingLayouts.length === 1) {
      // if single match, use it directly
      [pageLayout] = matchingLayouts;
    } else {
      // when multiple layouts found with same id  use allessehId to calculate the correct layoutId
      const calculatedLayoutId = metadata.allessehId.split('_').slice(2).join('_').toLowerCase();
      pageLayout = pageTypeSetting.layouts.find((l) => l.id === calculatedLayoutId);
    }
    // END CLEANUP(structure-migration)

    return pageLayout;
  }, [pageType, pageTypeSetting, pageContainers, metadata, resolvedLayoutId]);

  const allUsedUiModuleTypes = useMemo(
    () =>
      pageType !== PageType.Banner // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        ? getAllUiModuleTypes(pageContainers?.collection.map((module) => module.attributes.pageModule as PageModule))
        : [],
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    [pageType, getAllUiModuleTypes, pageContainers?.collection]
  );

  const addableUiModules = useMemo(
    () =>
      (currentLayout?.availableLayoutModules ?? []).filter((availableModule) => {
        const availableUiModuleType = availableModule.defaultModule.uiModuleType;
        const usedUiModulesForType = allUsedUiModuleTypes.filter(
          (usedUiModuleType) => usedUiModuleType === availableUiModuleType
        );
        return usedUiModulesForType.length < ((availableModule.maximumAllowed as number | undefined) ?? Infinity);
      }),
    [currentLayout, allUsedUiModuleTypes]
  );

  const moduleInfoByTreatmentType: Record<string, ModuleInfo> = useMemo(() => {
    const mapping: Record<string, ModuleInfo> = {};
    (currentLayout?.availableLayoutModules ?? []).forEach((module) => {
      const treatmentType = getTreatmentType(module.defaultModule);
      if (!(treatmentType in mapping)) {
        mapping[treatmentType] = {
          maximumAllowed: module.maximumAllowed,
          minimumAllowed: module.minimumAllowed,
          uiModuleType: module.defaultModule.uiModuleType!
        };
      }
    });
    return mapping;
  }, [currentLayout]);

  const addedModulesByTreatmentType = useMemo(() => {
    const mapping: Record<string, number> = {};

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    pageContainers?.collection?.forEach((container) => {
      const pageModule = container.attributes.pageModule as PageModule;
      const treatmentType = getTreatmentType(pageModule);

      if (!mapping[treatmentType]) {
        mapping[treatmentType] = 0;
      }
      mapping[treatmentType] += 1;
    });

    return mapping;
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  }, [pageContainers?.collection]);

  const removableUiModules = useMemo(
    () => [
      ...(currentLayout?.availableLayoutModules ?? []).filter((availableModule) => {
        if (availableModule.removable === false) return false;
        const availableUiModuleType = availableModule.defaultModule.uiModuleType;
        const usedUiModulesForType = allUsedUiModuleTypes.filter(
          (usedUiModuleType) => usedUiModuleType === availableUiModuleType
        );
        return usedUiModulesForType.length > ((availableModule.minimumAllowed as number | undefined) ?? 0);
      }),
      ...(currentLayout?.availableRequiredLayoutModules ?? [])
    ],
    [currentLayout, allUsedUiModuleTypes]
  );

  const availableRequiredLayoutModules = useMemo(
    () => currentLayout?.availableRequiredLayoutModules ?? undefined,
    [currentLayout]
  );

  const getTreatmentTypeSetting = useCallback(
    (pageModule: PageModule) => {
      const treatmentType = getTreatmentType(pageModule);
      // SETTINGS_ONLY(structure-migration): pageTypeSetting.treatmentTypeSettings comes from hardcoded SETTINGS.
      // For structure-only pages (no PageTypeSetting), fall back to the publication's structureTreatmentTypes catalog.
      const fromPageTypeSetting = pageTypeSetting?.treatmentTypeSettings.find(
        (t) => t.treatmentType === treatmentType || t.treatmentTypeKey === treatmentType
      );
      if (fromPageTypeSetting) return fromPageTypeSetting;
      // END SETTINGS_ONLY(structure-migration) — when deleted: remove fromPageTypeSetting block; always use structureTreatmentTypes.find below
      return structureTreatmentTypes.find(
        (t) => t.treatmentType === treatmentType || t.treatmentTypeKey === treatmentType
      ) as unknown as TreatmentTypeSetting | undefined;
    },
    [structureTreatmentTypes, pageTypeSetting]
  );

  const isSection = useMemo(() => pageType === PageType.Section, [pageType]);
  const isNewsletter = useMemo(() => pageType === PageType.Newsletter, [pageType]);

  const value = {
    pageTypeSetting,
    currentLayout,
    addableUiModules,
    removableUiModules,
    availableRequiredLayoutModules,
    getTreatmentTypeSetting,
    structureTreatmentTypes: structureTreatmentTypes as unknown as TreatmentTypeSetting[],
    isSection,
    isNewsletter,
    addedModulesByTreatmentType,
    moduleInfoByTreatmentType
  };
  return <PageTypeSettingsContext.Provider value={value}>{children}</PageTypeSettingsContext.Provider>;
};
